/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC } from "react";
import { styled, Box } from "@mui/material";
import { CustomDivider, getStartTimeOfTask } from "styles/TaskRegister";
import { DisplayItem } from "components/Common/DisplayItem";
import { MultipleChargeTasksItem } from "components/TaskRegisterMultipleCharge/hooks/GetMultipleChargeTasksHook";
import { useTaskRegisterDataContext } from "context/TaskRegisterDataProvider";
import { DisplayItemNode } from "components/Common/DisplayItemNode";
import { AwvJobsDisplay } from "./AwvJobDisplay";
import { aggregateAwvJobs } from "./utils";
import { useTranslation } from "react-i18next";

interface MultipleChargeConfirmPanelProps {
  tasks: MultipleChargeTasksItem;
  onAwvSelected?: (frameTypeAndNumber: string) => void;
}

/**
 * Component of selecting task side panel
 * @param{MultipleChargeConfirmPanelProps} param0 Properties
 * @return{FC} Component
 */
export const MultipleChargeConfirmPanel: FC<
  MultipleChargeConfirmPanelProps
> = ({ tasks, onAwvSelected }) => {
  const { pointNodes } = useTaskRegisterDataContext();
  const { t } = useTranslation();

  const awvJobs = aggregateAwvJobs(tasks);

  return (
    <Container>
      <ContentsSection>
        <DisplayItem title={t("AWVUI-MSG212")} value={tasks.jobsetName} />
        <DisplayItem
          title={t("AWVUI-MSG213")}
          value={
            pointNodes.find((i) => i.nodeId === tasks.startNodeId)?.label ??
            "--"
          }
        />
        <DisplayItem
          title={t("AWVUI-MSG214")}
          value={
            pointNodes.find((i) => i.nodeId === tasks.endNodeId)?.label ??
            t("AWVUI-MSG119")
          }
        />
        <DisplayItem
          title={t("AWVUI-MSG215")}
          value={getStartTimeOfTask(tasks.startDate, t("AWVUI-MSG59"))}
        />
        <DisplayItem
          title={t("AWVUI-MSG216")}
          value={getStartTimeOfTask(tasks.endDate, t("AWVUI-MSG59"))}
        />
        <DisplayItem
          title={t("AWVUI-MSG41")}
          value={tasks.memo ?? t("AWVUI-MSG119")}
        />
        <CustomDivider />
        <DisplayItemNode title={t("AWVUI-MSG203")}>
          <AwvJobsDisplay awvJobs={awvJobs} onAwvSelected={onAwvSelected} />
        </DisplayItemNode>
      </ContentsSection>
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const ContentsSection = styled(Box)(({ theme }) => ({
  width: "100%",
  borderBottom: "1px solid black",
  padding: "0.5rem 2rem",
}));
